import { fontWeight } from 'html2canvas/dist/types/css/property-descriptors/font-weight';
import React, { useMemo } from 'react';
import { Bar } from 'react-chartjs-2';

const clamp = (v: number, min: number, max: number) =>
    Math.max(min, Math.min(max, v));

export interface OnePagerBarChartProps {
    bgColors: any;
    barChartData: {
        region: string;
        data: Record<string, number>;
        projectCounts?: Record<string, number>;
    };
    size?: number;
}

const OnePagerBarChart = ({ bgColors, barChartData, size }: OnePagerBarChartProps) => {
    const { data: chartData, region, projectCounts } = barChartData;

    const labels = useMemo(() => Object.keys(chartData ?? {}), [chartData]);

    const values = useMemo(
        () => labels.map((k) => (typeof chartData?.[k] === 'number' ? (chartData as any)[k] : 0)),
        [labels, chartData]
    );

    const counts = useMemo(
        () => labels.map((k) => Number(projectCounts?.[k] ?? 0)),
        [labels, projectCounts]
    );

    const barCount = Math.max(1, labels.length);
    const grow = 1 + Math.min(barCount, 8);

    const maxBarThickness = clamp(Math.floor(180 / barCount), 6, 8);
    const barPercentage = clamp(0.9 - (barCount - 1) * 0.035, 0.25, 0.85);
    const categoryPercentage = 0.9;

    const rawMax = Math.max(...values, 0);
    const maxY = Math.ceil(rawMax + rawMax * 0.2 + 1) || 100;

    const data = {
        labels,
        datasets: [
            {
                label: 'Monto aprobado',
                data: values,
                backgroundColor: bgColors,
                borderWidth: 0,
                maxBarThickness,
            },
        ],
    };

    const options: any = {
        responsive: true,
        maintainAspectRatio: false,

        layout: {
            padding: { top: 28, bottom: 8 },
        },

        plugins: {
            datalabels: {
                display: true,
                anchor: 'end',
                align: 'top',
                offset: 6,
                formatter: (v: number) => `${v}M`,
                color: '#000',
                font: { size: 10 },
                clamp: false,
                clip: false,
            },
        },

        legend: { display: false },

        tooltips: {
            titleFontSize: 12,
            bodyFontSize: 12,
            callbacks: {
                label: (ti: any) => {
                    const idx = ti.index ?? ti.dataIndex ?? 0;
                    const val = ti.yLabel ?? ti.raw;
                    return `${ti.label}: ${val}M · Proyectos: ${counts[idx]}`;
                },
            },
        },

        scales: {
            xAxes: [
                {
                    gridLines: { display: false },
                    barPercentage,
                    categoryPercentage,
                    ticks: {
                        fontSize: 9,
                        fontColor: '#000',
                        maxRotation: 0,
                        minRotation: 0,
                        padding: 4,
                        callback: (value: string, index: number) => [value, String(counts[index] ?? 0)],
                    },
                },
            ],
            yAxes: [
                {
                    gridLines: { display: false },
                    ticks: {
                        display: false,
                        beginAtZero: true,
                        max: maxY,
                        stepSize: size && Math.ceil(maxY / size),
                    },
                },
            ],
        },
    };

    const sumValues = values.reduce((a, v) => a + v, 0);

    return (
        <div className="bar-chart-styles" style={{ flex: `${grow} 1 0%`, minWidth: 0, height: '390px' }}>
            <div className="chart-area">
                <Bar data={data} options={options} />
            </div>
            <div className="chart-footer">
                <span>{region}</span>
                <span>{sumValues.toFixed(1)} M</span>
            </div>
        </div>
    );
};

export default OnePagerBarChart;
